import { useState, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import StepWizard from "../components/StepWizard";
import FileUpload from "../components/FileUpload";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { RESUME_TEMPLATES } from "../components/ResumeTemplates";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { useCredits } from '../context/CreditsContext';
import BuyCreditsModal from '../components/BuyCreditsModal';
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  Download,
  RefreshCw,
  CheckCircle,
  Sparkles,
  FileText,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Optimizer = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [jobDescription, setJobDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [extractedText, setExtractedText] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const resultRef = useRef(null);
  const previewContainerRef = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState(
    "modern-professional"
  );
  const [structuredData, setStructuredData] = useState(null);
  const { credits, deductCredits, downloadCost, fetchCredits, hasEnoughCredits } = useCredits();
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  // Auto-save job description to localStorage
  const handleJDChange = useCallback((e) => {
    const value = e.target.value;
    setJobDescription(value);
    localStorage.setItem("draft_jd", value);
  }, []);

  // Load draft on mount
  useState(() => {
    const draft = localStorage.getItem("draft_jd");
    if (draft) {
      setJobDescription(draft);
    }
  }, []);

  const handleFileSelect = useCallback(
    async (file) => {
      setUploadError("");

      // Validate file size
      if (file.size > 5 * 1024 * 1024) {
        setUploadError("File size must be less than 5MB");
        return;
      }

      setSelectedFile(file);
      setIsUploading(true);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("user_id", user?.uid || "anonymous");

        const response = await axios.post(`${API}/upload-resume`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        setExtractedText(response.data.extracted_text);
        toast.success("Resume uploaded successfully!");
      } catch (error) {
        console.error("Upload error:", error);
        const errorMessage =
          error.response?.data?.detail || "Failed to upload resume";
        setUploadError(errorMessage);
        toast.error(errorMessage);
        setSelectedFile(null);
      } finally {
        setIsUploading(false);
      }
    },
    [user]
  );

  const handleClearFile = useCallback(() => {
    setSelectedFile(null);
    setExtractedText("");
    setUploadError("");
  }, []);

  const handleOptimize = useCallback(async () => {
    setIsOptimizing(true);
    setOptimizationProgress(0);

    const progressInterval = setInterval(() => {
      setOptimizationProgress((prev) =>
        prev >= 90 ? prev : prev + Math.random() * 15
      );
    }, 500);

    try {
      const response = await axios.post(`${API}/optimize`, {
        job_description: jobDescription,
        resume_text: extractedText,
      });

      setOptimizationProgress(100);
      setResult(response.data);
      setStructuredData(response.data.structured_data || null);
      setCurrentStep(4);
      toast.success("Resume optimized successfully!");
      localStorage.removeItem("draft_jd");
    } catch (error) {
      console.error("Optimization error:", error);
      toast.error(error.response?.data?.detail || "Failed to optimize resume");
    } finally {
      clearInterval(progressInterval);
      setIsOptimizing(false);
    }
  }, [jobDescription, extractedText]);

  const handleDownloadTemplatePDF = useCallback(async (templateId) => {
    // Check credits first
    if (!hasEnoughCredits) {
      setShowBuyCredits(true);
      return;
    }
  
    const previewElement = previewContainerRef.current;
    
    if (!previewElement) {
      toast.error('Preview not available');
      return;
    }
  
    // Deduct credits before download
    const deductResult = await deductCredits(downloadCost);
    
    if (!deductResult.success) {
      if (deductResult.insufficient) {
        setShowBuyCredits(true);
        toast.error(`You need ${deductResult.required} credits. You have ${deductResult.current}.`);
      } else {
        toast.error('Failed to process download');
      }
      return;
    }
  
    const toastId = toast.loading('Generating PDF...');
  
    try {
      // ... rest of your existing PDF generation code ...
      // (Keep all the existing html2canvas and jsPDF logic)
      
      // Clone the preview element to avoid modifying the original
      const clonedElement = previewElement.cloneNode(true);
      clonedElement.style.width = '794px';
      clonedElement.style.maxHeight = 'none';
      clonedElement.style.overflow = 'visible';
      clonedElement.style.position = 'absolute';
      clonedElement.style.left = '-9999px';
      clonedElement.style.top = '0';
      clonedElement.style.backgroundColor = '#ffffff';
      document.body.appendChild(clonedElement);
  
      await new Promise(resolve => setTimeout(resolve, 500));
  
      const canvas = await html2canvas(clonedElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: 794,
        windowWidth: 794,
      });
  
      document.body.removeChild(clonedElement);
  
      const imgData = canvas.toDataURL('image/png', 1.0);
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
        hotfixes: ['px_scaling']
      });
  
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width / 2;
      const imgHeight = canvas.height / 2;
      
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      const pageCount = Math.ceil(scaledHeight / pdfHeight);
  
      for (let i = 0; i < pageCount; i++) {
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -(i * pdfHeight), pdfWidth, scaledHeight, undefined, 'FAST');
      }
  
      pdf.save(`resume-${templateId}.pdf`);
      toast.success(`PDF downloaded! ${downloadCost} credits used.`, { id: toastId });
      
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Credits refunded.', { id: toastId });
      // Note: In production, you might want to implement a refund mechanism
    }
  }, [hasEnoughCredits, deductCredits, downloadCost]);


  const handleCopyResume = useCallback(async () => {
    if (!result?.optimized_resume) return;

    try {
      await navigator.clipboard.writeText(result.optimized_resume);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  }, [result]);

  const handleDownloadPDF = useCallback(() => {
    if (!result?.optimized_resume) return;

    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
      });

      // Page dimensions in points
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginLeft = 50;
      const marginRight = 50;
      const marginTop = 50;
      const marginBottom = 50;
      const contentWidth = pageWidth - marginLeft - marginRight;
      let yPosition = marginTop;

      // Colors
      const primaryColor = [33, 37, 41]; // Dark gray for headings
      const secondaryColor = [73, 80, 87]; // Medium gray for body
      const accentColor = [0, 82, 147]; // Blue for section headers

      // Clean resume text
      let resumeText = result.optimized_resume || "";
      resumeText = resumeText.replace(/\*\*/g, "");
      resumeText = resumeText.replace(/##/g, "");
      resumeText = resumeText.replace(/\*/g, "");
      resumeText = resumeText.replace(/`/g, "");

      const lines = resumeText.split("\n");

      // Helper: Add new page if needed
      const checkPageBreak = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - marginBottom) {
          doc.addPage();
          yPosition = marginTop;
          return true;
        }
        return false;
      };

      // Helper: Draw text with word wrap
      const drawText = (text, fontSize, fontStyle, color, lineHeight) => {
        doc.setFontSize(fontSize);
        doc.setFont("helvetica", fontStyle);
        doc.setTextColor(...color);

        const textLines = doc.splitTextToSize(text, contentWidth);
        textLines.forEach((line) => {
          checkPageBreak(lineHeight + 2);
          doc.text(line, marginLeft, yPosition);
          yPosition += lineHeight;
        });
      };

      // Section headers detection
      const sectionHeaders = [
        "PROFESSIONAL SUMMARY",
        "SUMMARY",
        "PROFILE",
        "OBJECTIVE",
        "TECHNICAL SKILLS",
        "SKILLS",
        "CORE COMPETENCIES",
        "TECHNOLOGIES",
        "PROFESSIONAL EXPERIENCE",
        "EXPERIENCE",
        "WORK EXPERIENCE",
        "EMPLOYMENT HISTORY",
        "EDUCATION",
        "ACADEMIC BACKGROUND",
        "ACADEMIC",
        "CERTIFICATIONS",
        "CERTIFICATES",
        "LICENSES",
        "CREDENTIALS",
        "PROJECTS",
        "KEY PROJECTS",
        "NOTABLE PROJECTS",
        "PUBLICATIONS",
        "AWARDS",
        "ACHIEVEMENTS",
      ];

      const isSectionHeader = (text) => {
        const cleanText = text.toUpperCase().trim();
        return sectionHeaders.some(
          (header) => cleanText === header || cleanText.startsWith(header + " ")
        );
      };

      // Check if line is job title (contains | and dates pattern)
      const isJobTitle = (text) => {
        return (
          text.includes("|") &&
          (text.includes("20") ||
            text.includes("19") ||
            text.toLowerCase().includes("present")) &&
          !text.includes("@") &&
          !text.toLowerCase().includes("http")
        );
      };

      // Check if contact line
      const isContactLine = (text, lineNum) => {
        return (
          lineNum <= 4 &&
          (text.includes("|") ||
            text.includes("@") ||
            text.toLowerCase().includes("linkedin"))
        );
      };

      let processedFirstLine = false;
      let lineNumber = 0;

      for (const line of lines) {
        const trimmedLine = line.trim();
        lineNumber++;

        // Skip empty lines but add spacing
        if (!trimmedLine) {
          yPosition += 8;
          continue;
        }

        // Skip dividers
        if (
          trimmedLine === "---" ||
          trimmedLine === "___" ||
          trimmedLine.match(/^-+$/)
        ) {
          continue;
        }

        // Skip metadata sections at end
        if (
          trimmedLine.toLowerCase().includes("keywords added") ||
          trimmedLine.toLowerCase().includes("optimization summary")
        ) {
          break;
        }

        // Candidate Name (first substantial line)
        if (
          !processedFirstLine &&
          trimmedLine.length > 2 &&
          !trimmedLine.startsWith("-")
        ) {
          checkPageBreak(35);
          doc.setFontSize(22);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...primaryColor);
          doc.text(trimmedLine, marginLeft, yPosition);
          yPosition += 28;
          processedFirstLine = true;
          continue;
        }

        // Contact Information Line
        if (isContactLine(trimmedLine, lineNumber)) {
          checkPageBreak(18);
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(100, 100, 100);

          // Clean up contact line formatting
          const contactText = trimmedLine
            .replace(/\s*\|\s*/g, "   •   ")
            .replace(/\s{2,}/g, "   ");

          const contactLines = doc.splitTextToSize(contactText, contentWidth);
          contactLines.forEach((cLine) => {
            doc.text(cLine, marginLeft, yPosition);
            yPosition += 12;
          });
          yPosition += 8;
          continue;
        }

        // Section Headers
        if (isSectionHeader(trimmedLine)) {
          checkPageBreak(35);
          yPosition += 15;

          // Section header text
          doc.setFontSize(11);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...accentColor);
          doc.text(trimmedLine.toUpperCase(), marginLeft, yPosition);

          // Underline
          yPosition += 4;
          doc.setDrawColor(...accentColor);
          doc.setLineWidth(1.5);
          doc.line(marginLeft, yPosition, marginLeft + 100, yPosition);

          yPosition += 15;
          continue;
        }

        // Job Title Lines (Company | Role | Date format)
        if (isJobTitle(trimmedLine)) {
          checkPageBreak(25);
          yPosition += 10;

          doc.setFontSize(11);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...primaryColor);

          const titleLines = doc.splitTextToSize(trimmedLine, contentWidth);
          titleLines.forEach((tLine) => {
            doc.text(tLine, marginLeft, yPosition);
            yPosition += 14;
          });
          yPosition += 4;
          continue;
        }

        // Bullet Points
        if (trimmedLine.startsWith("-") || trimmedLine.startsWith("•")) {
          checkPageBreak(20);

          const bulletText = trimmedLine.replace(/^[-•]\s*/, "").trim();

          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...secondaryColor);

          // Draw bullet
          doc.setFillColor(...secondaryColor);
          doc.circle(marginLeft + 3, yPosition - 3, 1.5, "F");

          // Draw text with proper indent
          const bulletIndent = 15;
          const bulletWidth = contentWidth - bulletIndent;
          const bulletLines = doc.splitTextToSize(bulletText, bulletWidth);

          bulletLines.forEach((bLine, idx) => {
            if (idx > 0) checkPageBreak(14);
            doc.text(bLine, marginLeft + bulletIndent, yPosition);
            yPosition += 14;
          });
          yPosition += 3;
          continue;
        }

        // Skills with Categories (contains colon)
        if (
          trimmedLine.includes(":") &&
          !trimmedLine.toLowerCase().startsWith("http")
        ) {
          checkPageBreak(20);

          const colonIndex = trimmedLine.indexOf(":");
          const category = trimmedLine.substring(0, colonIndex).trim();
          const skills = trimmedLine.substring(colonIndex + 1).trim();

          doc.setFontSize(10);

          // Category label
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...primaryColor);
          doc.text(category + ":", marginLeft, yPosition);

          // Skills value
          const categoryWidth = doc.getTextWidth(category + ": ");
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...secondaryColor);

          const skillsWidth = contentWidth - categoryWidth - 5;
          const skillLines = doc.splitTextToSize(skills, skillsWidth);

          skillLines.forEach((sLine, idx) => {
            if (idx === 0) {
              doc.text(sLine, marginLeft + categoryWidth, yPosition);
            } else {
              yPosition += 13;
              checkPageBreak(13);
              doc.text(sLine, marginLeft + categoryWidth, yPosition);
            }
          });
          yPosition += 16;
          continue;
        }

        // Regular paragraph text
        checkPageBreak(18);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...secondaryColor);

        const textLines = doc.splitTextToSize(trimmedLine, contentWidth);
        textLines.forEach((textLine) => {
          doc.text(textLine, marginLeft, yPosition);
          yPosition += 14;
        });
        yPosition += 4;
      }

      // Add page numbers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 25, {
          align: "center",
        });
      }

      // Save the PDF
      doc.save("optimized-resume.pdf");
      toast.success("Professional PDF downloaded!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  }, [result]);

  const handleStartOver = useCallback(() => {
    setCurrentStep(1);
    setJobDescription("");
    setSelectedFile(null);
    setExtractedText("");
    setResult(null);
    setOptimizationProgress(0);
    localStorage.removeItem("draft_jd");
  }, []);

  const canProceedStep1 = jobDescription.trim().length >= 50;
  const canProceedStep2 = extractedText.trim().length > 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <StepWizard currentStep={currentStep} />

          {/* Step 1: Job Description */}
          {currentStep === 1 && (
            <Card
              className="border-slate-200 shadow-sm"
              data-testid="step-1-card"
            >
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  Paste the Job Description
                </CardTitle>
                <CardDescription className="text-slate-600">
                  Copy and paste the complete job posting you want to apply for
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Textarea
                    placeholder="Paste the full job description here...

                    Example:
                    We are looking for a Senior Software Engineer to join our team...
                    Requirements:
                    - 5+ years of experience with React
                    - Strong knowledge of TypeScript
                    ..."
                    value={jobDescription}
                    onChange={handleJDChange}
                    className="min-h-[300px] bg-white border-slate-200 focus:border-accent focus:ring-accent/20 text-slate-900 placeholder:text-slate-400"
                    data-testid="jd-textarea"
                  />
                  <div className="absolute bottom-3 right-3 text-sm text-slate-400">
                    {jobDescription.length.toLocaleString()} characters
                  </div>
                </div>

                {jobDescription.length > 0 && jobDescription.length < 50 && (
                  <p className="text-sm text-amber-600">
                    Please enter at least 50 characters (currently{" "}
                    {jobDescription.length})
                  </p>
                )}

                <div className="flex justify-end">
                  <Button
                    onClick={() => setCurrentStep(2)}
                    disabled={!canProceedStep1}
                    className="bg-accent hover:bg-accent/90 text-white font-semibold px-8"
                    data-testid="step-1-next"
                  >
                    Next: Upload Resume
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Resume Upload */}
          {currentStep === 2 && (
            <Card
              className="border-slate-200 shadow-sm"
              data-testid="step-2-card"
            >
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  Upload Your Resume
                </CardTitle>
                <CardDescription className="text-slate-600">
                  Upload your existing resume in PDF format (max 5MB)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <FileUpload
                  onFileSelect={handleFileSelect}
                  selectedFile={selectedFile}
                  onClear={handleClearFile}
                  error={uploadError}
                />

                {isUploading && (
                  <div className="flex items-center justify-center gap-3 py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-accent" />
                    <span className="text-slate-600">
                      Extracting text from PDF...
                    </span>
                  </div>
                )}

                {extractedText && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">
                        Resume text extracted successfully!
                      </span>
                    </div>
                    <p className="text-sm text-green-600">
                      {extractedText.length.toLocaleString()} characters
                      extracted
                    </p>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button
                    onClick={() => setCurrentStep(1)}
                    variant="outline"
                    className="px-6"
                    data-testid="step-2-back"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setCurrentStep(3)}
                    disabled={!canProceedStep2 || isUploading}
                    className="bg-accent hover:bg-accent/90 text-white font-semibold px-8"
                    data-testid="step-2-next"
                  >
                    Next: Optimize
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Optimization */}
          {currentStep === 3 && (
            <Card
              className="border-slate-200 shadow-sm"
              data-testid="step-3-card"
            >
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  {isOptimizing
                    ? "Optimizing Your Resume"
                    : "Ready to Optimize"}
                </CardTitle>
                <CardDescription className="text-slate-600">
                  {isOptimizing
                    ? "Our AI is analyzing and optimizing your resume..."
                    : "Review your inputs and start the optimization process"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {isOptimizing ? (
                  <div className="py-8">
                    <div className="flex flex-col items-center gap-6">
                      <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center">
                        <Sparkles className="h-10 w-10 text-accent animate-pulse" />
                      </div>
                      <div className="w-full max-w-md">
                        <Progress
                          value={optimizationProgress}
                          className="h-2"
                        />
                        <p className="text-center text-sm text-slate-500 mt-2">
                          {Math.round(optimizationProgress)}% complete
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-600">
                          Analyzing job requirements and tailoring your
                          resume...
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          This may take a moment
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4 text-slate-500" />
                          <span className="font-medium text-slate-700">
                            Job Description
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-3">
                          {jobDescription.substring(0, 200)}...
                        </p>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4 text-slate-500" />
                          <span className="font-medium text-slate-700">
                            Your Resume
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">
                          {selectedFile?.name} (
                          {extractedText.length.toLocaleString()} characters)
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <Button
                        onClick={() => setCurrentStep(2)}
                        variant="outline"
                        className="px-6"
                        data-testid="step-3-back"
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                      </Button>
                      <Button
                        onClick={handleOptimize}
                        className="bg-accent hover:bg-accent/90 text-white font-semibold px-8"
                        data-testid="optimize-button"
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Optimize Resume
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 4: Results */}
          {/* Step 4: Results */}
          {currentStep === 4 && result && (
            <div className="space-y-6" data-testid="step-4-results">
              {/* Success Banner */}
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-green-800">
                        Resume Optimized Successfully!
                      </h3>
                      <p className="text-sm text-green-600">
                        Choose a template and download your resume
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Keywords */}
              {result.keywords?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      Keywords Integrated
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {result.keywords.map((keyword, i) => (
                        <Badge
                          key={i}
                          variant="secondary"
                          className="bg-blue-50 text-blue-700"
                        >
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Template Selection */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">
                    Choose Your Template
                  </CardTitle>
                  <CardDescription>
                    Select a design style for your optimized resume
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {RESUME_TEMPLATES.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => setSelectedTemplate(template.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                          selectedTemplate === template.id
                            ? "border-blue-500 bg-blue-50 shadow-md"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <p className="font-semibold text-slate-800">
                          {template.name}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {template.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Preview & Download */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      Preview:{" "}
                      {
                        RESUME_TEMPLATES.find((t) => t.id === selectedTemplate)
                          ?.name
                      }
                    </CardTitle>
                    <CardDescription>Review before downloading</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleCopyResume}
                      className="gap-2"
                    >
                      {copied ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                      {copied ? "Copied!" : "Copy Text"}
                    </Button>
                    <Button
                      onClick={() =>
                        handleDownloadTemplatePDF(selectedTemplate)
                      }
                      className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleStartOver}
                      className="gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Start Over
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                <div 
                      ref={previewContainerRef}
                      data-resume-preview="true"
                      className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-inner"
                      style={{ maxHeight: '800px', overflowY: 'auto' }}
                    >
                    {structuredData || result?.structured_data ? (
                      (() => {
                        const template = RESUME_TEMPLATES.find(
                          (t) => t.id === selectedTemplate
                        );
                        const TemplateComponent = template?.component;
                        return TemplateComponent ? (
                          <TemplateComponent
                            data={structuredData || result.structured_data}
                          />
                        ) : (
                          <div className="p-8 text-slate-600">
                            Template not found
                          </div>
                        );
                      })()
                    ) : (
                      <div className="p-8">
                        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700">
                          {result.optimized_resume}
                        </pre>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
      <BuyCreditsModal 
        isOpen={showBuyCredits} 
        onClose={() => setShowBuyCredits(false)}
        onSuccess={() => fetchCredits()}
      />
    </div>
  );
};

export default Optimizer;
